import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Logo from "@/components/Logo";
import LoginButton from "@/components/LoginButton";
import UserMenu from "@/components/UserMenu";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/auth";

/**
 * TopNav — the learner app's single global header.
 *
 * Visual treatment matches apps/marketing's TopNav (slate primary, amber
 * underline for active, hexagon mark + wordmark on the left). Adds an
 * auth-aware CTA slot on the right: LoginButton when anonymous, UserMenu
 * when authenticated, and nothing (to avoid a flash) while loading.
 */
export default function TopNav() {
  const { t, i18n } = useTranslation();
  const { state } = useAuth();

  return (
    <header className="border-b border-brand-primary/10 bg-white">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-md py-sm sm:px-lg sm:py-md">
        <Link to="/" className="no-underline" aria-label="RoadBridge home">
          <Logo />
        </Link>
        <nav aria-label="Primary" className="flex items-center gap-sm sm:gap-md">
          <ul className="flex items-center gap-sm text-sm font-medium sm:gap-md">
            <li>
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `no-underline rounded-sm px-xs py-xs transition-colors ${
                    isActive
                      ? "text-brand-primary underline underline-offset-4 decoration-brand-accent decoration-2"
                      : "text-neutral-600 hover:text-brand-primary hover:underline"
                  }`
                }
              >
                {t("nav.home")}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `no-underline rounded-sm px-xs py-xs transition-colors ${
                    isActive
                      ? "text-brand-primary underline underline-offset-4 decoration-brand-accent decoration-2"
                      : "text-neutral-600 hover:text-brand-primary hover:underline"
                  }`
                }
              >
                {t("nav.about")}
              </NavLink>
            </li>
            {state.kind === "authenticated" && (
              <li>
                <NavLink
                  to="/training"
                  className={({ isActive }) =>
                    `no-underline rounded-sm px-xs py-xs transition-colors ${
                      isActive
                        ? "text-brand-primary underline underline-offset-4 decoration-brand-accent decoration-2"
                        : "text-neutral-600 hover:text-brand-primary hover:underline"
                    }`
                  }
                >
                  {t("nav.training")}
                </NavLink>
              </li>
            )}
          </ul>
          <LanguageSwitcher
            currentLang={i18n.language}
            onChange={(l) => void i18n.changeLanguage(l)}
          />
          <AuthSlot state={state.kind} />
        </nav>
      </div>
    </header>
  );
}

function AuthSlot({ state }: { state: "loading" | "anonymous" | "authenticated" }) {
  if (state === "loading") {
    // Reserve the approximate width so the header doesn't reflow when auth
    // resolves. A real skeleton ships in ROADS-147+.
    return <span className="inline-block h-8 w-24" aria-hidden="true" />;
  }
  if (state === "authenticated") return <UserMenu />;
  return <LoginButton />;
}

